import { StyleDeclarationValue, css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useEventLogging } from '../../../../models/Logging';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { AccountOperationsViewModel, AdminPermission } from '../../../../viewmodels/AdminViewModels';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { TextInput } from '../../../../web/components/TextInput';
import { baseStyleSheet } from '../../../../web/styles/styles';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../../web/components-v2/Dialog';
import { ConditionalAccess } from '../../ConditionalAccess';
import { AccountDetailsInfoField } from '../AccountDetailsInfoField';
import { useAdminAccountAddressMutation } from '../../../../queries/Admin/Account/useAdminAccountAddressMutation';
import { styleSheet } from './styles';

interface IProps {
	styles?: StyleDeclarationValue[];
	accountOperations: AccountOperationsViewModel;
}

export function AddressField({ styles, accountOperations }: IProps) {
	const [isDialogOpen, setIsDialogOpen] = React.useState(false);
	const [address1, setAddress1] = React.useState('');
	const [address2, setAddress2] = React.useState('');
	const [city, setCity] = React.useState('');
	const [stateProvince, setStateProvince] = React.useState('');
	const [postalCode, setPostalCode] = React.useState('');
	const [country, setCountry] = React.useState('');

	const errorMessages = useErrorMessages();
	const { logApiError } = useEventLogging('AddressField');
	const addressMutation = useAdminAccountAddressMutation();

	const planDetails = accountOperations?.account?.planDetails;
	const address = planDetails?.address;
	const isBusy = addressMutation.isPending;

	const hasAddress =
		address?.address1 || address?.city || address?.stateProvince || address?.postalCode || address?.country;

	const handleOpenDialog = () => {
		setAddress1(address?.address1 || '');
		setAddress2(address?.address2 || '');
		setCity(address?.city || '');
		setStateProvince(address?.stateProvince || '');
		setPostalCode(address?.postalCode || '');
		setCountry(address?.country || '');
		setIsDialogOpen(true);
	};

	const canSave = city.trim() && stateProvince.trim();

	const handleSave = async () => {
		const accountId = accountOperations?.account?.id;
		if (!accountId || !canSave) return;

		const trimmedAddress = {
			address1: address1.trim() || undefined,
			address2: address2.trim() || undefined,
			city: city.trim() || undefined,
			stateProvince: stateProvince.trim() || undefined,
			postalCode: postalCode.trim() || undefined,
			country: country.trim() || undefined,
		};

		try {
			await addressMutation.mutateAsync({
				accountId,
				address: trimmedAddress,
			});

			if (planDetails) {
				planDetails.address = trimmedAddress;
				planDetails.addressOverridden = true;
			}

			toast.success('Address saved');
			setIsDialogOpen(false);
		} catch (error) {
			errorMessages.pushApiError(error);
			logApiError('UpdateAddress-Error', error);
		}
	};

	const labelContent = (
		<>
			Address
			{hasAddress && !planDetails?.addressOverridden ? (
				<span className={css(styleSheet.chargifyIndicator)}> (From Chargify)</span>
			) : null}
		</>
	);

	return (
		<>
			<AccountDetailsInfoField label={labelContent} className={css(styleSheet.container, ...(styles || []))}>
				<ConditionalAccess permission={AdminPermission.ModifyAccount} elseChildren={null}>
					{hasAddress ? (
						<>
							<div>
								{address?.address1 ? (
									<>
										{address.address1}
										<br />
									</>
								) : null}
								{address?.address2 ? (
									<>
										{address.address2}
										<br />
									</>
								) : null}
								{address?.city ? address.city + ', ' : null}
								{address?.stateProvince ? address.stateProvince + ' ' : null}
								{address?.postalCode ?? null}
								{(address?.city || address?.stateProvince || address?.postalCode) && address?.country ? <br /> : null}
								{address?.country ?? null}
							</div>
							<div className={css(styleSheet.actions)}>
								<button className={css(baseStyleSheet.brandLink)} onClick={handleOpenDialog}>
									<span>Edit</span>
								</button>
							</div>
						</>
					) : (
						<button className={css(baseStyleSheet.brandLink)} onClick={handleOpenDialog}>
							<span>Add Address</span>
						</button>
					)}
				</ConditionalAccess>
			</AccountDetailsInfoField>
			<Dialog open={isDialogOpen} onOpenChange={open => !open && setIsDialogOpen(false)}>
				<DialogPopup>
					<DialogHeader>
						<DialogTitle>{hasAddress ? 'Edit Address' : 'Add Address'}</DialogTitle>
					</DialogHeader>
					<DialogBody>
						<div className={css(styleSheet.formGrid)}>
							<label className={css(styleSheet.fieldLabel)}>Address Line 1</label>
							<TextInput
								className={css(styleSheet.input)}
								disabled={isBusy}
								inputId='address-field-address1'
								onChange={e => setAddress1(e.target.value)}
								type='text'
								value={address1}
								placeholder='Address Line 1'
							/>
							<label className={css(styleSheet.fieldLabel)}>Address Line 2</label>
							<TextInput
								className={css(styleSheet.input)}
								disabled={isBusy}
								inputId='address-field-address2'
								onChange={e => setAddress2(e.target.value)}
								type='text'
								value={address2}
								placeholder='Address Line 2'
							/>
							<div className={css(styleSheet.row)}>
								<div className={css(styleSheet.fieldGroup)}>
									<label className={css(styleSheet.fieldLabel)}>
										City <span className={css(styleSheet.required)}>*</span>
									</label>
									<TextInput
										className={css(styleSheet.input)}
										disabled={isBusy}
										inputId='address-field-city'
										onChange={e => setCity(e.target.value)}
										type='text'
										value={city}
										placeholder='City'
									/>
								</div>
								<div className={css(styleSheet.fieldGroup)}>
									<label className={css(styleSheet.fieldLabel)}>
										State/Province <span className={css(styleSheet.required)}>*</span>
									</label>
									<TextInput
										className={css(styleSheet.input)}
										disabled={isBusy}
										inputId='address-field-state'
										onChange={e => setStateProvince(e.target.value)}
										type='text'
										value={stateProvince}
										placeholder='State/Province'
									/>
								</div>
							</div>
							<div className={css(styleSheet.row)}>
								<div className={css(styleSheet.fieldGroup)}>
									<label className={css(styleSheet.fieldLabel)}>Postal Code</label>
									<TextInput
										className={css(styleSheet.input)}
										disabled={isBusy}
										inputId='address-field-postal'
										onChange={e => setPostalCode(e.target.value)}
										type='text'
										value={postalCode}
										placeholder='Postal Code'
									/>
								</div>
								<div className={css(styleSheet.fieldGroup)}>
									<label className={css(styleSheet.fieldLabel)}>Country</label>
									<TextInput
										className={css(styleSheet.input)}
										disabled={isBusy}
										inputId='address-field-country'
										onChange={e => setCountry(e.target.value)}
										type='text'
										value={country}
										placeholder='Country'
									/>
								</div>
							</div>
						</div>
					</DialogBody>
					<DialogFooter>
						<button
							className={css(baseStyleSheet.ctaButtonReverseSmall)}
							disabled={isBusy}
							onClick={() => setIsDialogOpen(false)}
						>
							<span>Cancel</span>
						</button>
						<button className={css(baseStyleSheet.ctaButtonSmall)} disabled={isBusy || !canSave} onClick={handleSave}>
							{isBusy ? <LoadingSpinner type='tiny' /> : <span>Save</span>}
						</button>
					</DialogFooter>
				</DialogPopup>
			</Dialog>
		</>
	);
}
